import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { LOAN_CALCULATOR_INITIAL_VALUES, LoanCalculatorActions } from "./types";
import { LoanCalculatorReducer } from "./reducer";
import { useLoanProducts } from "../../hooks";
import { LoanProductType } from "../../hooks/api/types";
import { ButtonLoaderIcon } from "../../assets/icons";
import { useLoanApplication } from "../loanApplication";

const LoanCalculatorContext = createContext(LOAN_CALCULATOR_INITIAL_VALUES);

type Props = {
  children: React.ReactNode;
};

const Loader = () => {
  return (
    <div className="cx-w-full cx-h-screen cx-flex cx-justify-center cx-items-center cx-text-brand-primary-regular">
      <ButtonLoaderIcon className="cx-animate-spin cx-w-6 cx-h-6" />
    </div>
  );
};

export const LoanCalculatorProvider: React.FC<Props> = ({ children }) => {
  const [state, dispatch] = useReducer(
    LoanCalculatorReducer,
    LOAN_CALCULATOR_INITIAL_VALUES["state"]
  );

  const { data: loanProducts } = useLoanProducts();
  const {
    state: {
      fixedTermLoan,
      revenueBasedLoan,
      loanType,
      invoices,
      buyNowPayLaterLoan,
    },
    actions: { update },
  } = useLoanApplication();

  useEffect(() => {
    update({ loading: true }, { local: true }).then();
    if (loanProducts?.loanConfig.length) {
      const fixedTermProduct = loanProducts?.loanConfig.find(
        (product: any) => product.type === LoanProductType.FIXED_TERM
      );
      const invoiceDiscountingProduct = loanProducts?.loanConfig.find(
        (product: any) => product.type === LoanProductType.INVOICE_DISCOUNTING
      );
      const payableFinancingProduct = loanProducts?.loanConfig.find(
        (product: any) => product.type === LoanProductType.PAYABLE_FINANCING
      );
      const revenueBasedProduct = loanProducts?.loanConfig.find(
        (product: any) =>
          product.type === LoanProductType.REVENUE_BASED_FINANCING
      );
      const bnplProduct = loanProducts?.loanConfig.find(
        (product: any) => product.type === LoanProductType.BNPL
      );

      dispatch({
        type: "SET_CONSTRAINTS",
        payload: {
          fixedTermLoan: fixedTermProduct?.config,
          invoiceDiscountingLoan: invoiceDiscountingProduct?.config,
          payableFinancingLoan: payableFinancingProduct?.config,
          revenueBasedLoan: revenueBasedProduct?.config,
          buyNowPayLaterLoan: bnplProduct?.config,
        },
      });

      update({ loading: false }, { local: true }).then();
    }
  }, [loanProducts?.loanConfig]);

  useEffect(() => {
    switch (loanType) {
      case LoanProductType.FIXED_TERM: {
        dispatch({
          type: "SET_FIXED_TERM_LOAN",
          payload: fixedTermLoan,
        });
        break;
      }
      case LoanProductType.REVENUE_BASED_FINANCING: {
        dispatch({
          type: "SET_REVENUE_BASED_LOAN",
          payload: revenueBasedLoan,
        });
        break;
      }
      case LoanProductType.INVOICE_DISCOUNTING: {
        if (invoices) {
          dispatch({
            type: "SET_INVOICE_DISCOUNTING_LOAN",
            payload: {
              invoices: invoices.map((invoice) => ({
                ...invoice,
                discountRate: invoice.discountRate,
                ...(invoice.invoiceDueDate && {
                  invoiceDueDate: invoice.invoiceDueDate,
                }),
              })),
            },
          });
        } else {
          dispatch({
            type: "SET_INVOICE_DISCOUNTING_LOAN",
            payload: { invoices: [] },
          });
        }
        break;
      }
      case LoanProductType.PAYABLE_FINANCING: {
        if (invoices) {
          dispatch({
            type: "SET_PAYABLE_FINANCING_LOAN",
            payload: {
              invoices: invoices.map((invoice) => ({
                ...invoice,
                discountRate: invoice.discountRate,
                ...(invoice.invoiceDueDate && {
                  invoiceDueDate: invoice.invoiceDueDate,
                }),
              })),
            },
          });
        } else {
          dispatch({
            type: "SET_PAYABLE_FINANCING_LOAN",
            payload: { invoices: [] },
          });
        }
        break;
      }
      case LoanProductType.BNPL: {
        dispatch({
          type: "SET_BNPL_LOAN",
          payload: buyNowPayLaterLoan,
        });
        break;
      }
    }
  }, [loanType, fixedTermLoan, invoices, revenueBasedLoan]);

  const actions = useMemo<LoanCalculatorActions>(
    () => ({
      setFixedTermLoan: (loan) => {
        dispatch({
          type: "SET_FIXED_TERM_LOAN",
          payload: loan,
        });
      },
      setRevenueBasedLoan: (loan) => {
        dispatch({
          type: "SET_REVENUE_BASED_LOAN",
          payload: loan,
        });
      },
      setInvoiceDiscountingLoan: (loan) => {
        dispatch({
          type: "SET_INVOICE_DISCOUNTING_LOAN",
          payload: loan,
        });
      },
      setPayableFinancingLoan: (loan) => {
        dispatch({
          type: "SET_PAYABLE_FINANCING_LOAN",
          payload: loan,
        });
      },
      setBuyNowPayLaterLoan: (loan) => {
        dispatch({
          type: "SET_BNPL_LOAN",
          payload: loan,
        });
      },
      setLoading: (loading) => {
        dispatch({ type: "SET_LOADING", payload: loading });
      },
    }),
    [dispatch]
  );

  const contextValue = useMemo(() => ({ state, actions }), [state, actions]);

  if (state.loading) {
    return <Loader />;
  }

  return (
    <LoanCalculatorContext.Provider value={contextValue}>
      {children}
    </LoanCalculatorContext.Provider>
  );
};

export const useLoanCalculator = () => useContext(LoanCalculatorContext);
